import {
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
  type RefObject,
} from 'react';
import { Button } from '@/components/ui/button';
import { Callout } from '@/components/ui/content';
import { StatusPill } from '@/components/ui/kit';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import appPageCopy, { type AppPageCopyKey } from '@/i18n/ns/app-page';
import readyCopy from '@/i18n/ns/ready';
import {
  activeTab,
  availableActions,
  nextTab,
  visibleTabs,
  type AppAction,
  type AppPageRights,
  type AppTab,
} from './page-model';
import { useMenuBehavior } from '../menu-behavior';
import { CanvasHeaderTools } from '../../canvas-footer';
import './app-page.css';

/** The handler each action needs; without it the action is not offered. */
const ACTION_HANDLER: Record<AppAction, keyof AppPageHandlers> = {
  duplicate: 'onDuplicate',
  reset: 'onReset',
  unshare: 'onUnshare',
  'change-in-chat': 'onChangeInChat',
  'open-chat': 'onOpenChat',
};

const TAB_KEY: Record<AppTab, AppPageCopyKey> = {
  app: 'tabApp',
  data: 'tabData',
  history: 'tabHistory',
  versions: 'tabVersions',
};

export type AppPageFailure = {
  version: number;
  people: number;
  /** A time already formatted for the viewer. */
  time: string;
  /** The version "Go back" would restore for everyone; null when there is none. */
  previousVersion: number | null;
};

export type AppPageHandlers = {
  onBack: () => void;
  onDuplicate: () => void;
  onReset: () => void;
  onUnshare: () => void;
  onChangeInChat: () => void;
  onOpenChat: () => void;
  /** Roll everyone back to the previous version (a Versions restore, then publish). */
  onGoBack: () => void;
  onFixInChat: () => void;
  onDetails: () => void;
};

export type AppPageProps = {
  app: { name: string; description: string; ownerName: string; icon?: ReactNode };
  savedVersion?: number;
  rights: AppPageRights;
  tab: AppTab;
  onTab: (tab: AppTab) => void;
  /** Tab content. A tab without a slot renders nothing: the owner of that content supplies it. */
  slots: Partial<Record<AppTab, ReactNode>>;
  /** The published version stopped loading; shown to editors and owners above the app. */
  failure: AppPageFailure | null;
  /** The handlers the host really has. A control whose handler is absent is not shown: never a dead button. */
  on: Partial<AppPageHandlers>;
  /** On the App's own page its actions sit in the page's one top bar (the canvas header), not a second header. */
  actionsInBar?: boolean;
  /** The document is a Deck: slides and Versions only, no Draft/Ready lifecycle, no App tabs. */
  deck?: boolean;
};

function ActionsPlace({ inBar, children }: { inBar: boolean; children: ReactNode }) {
  const actions = <div className="pc-apppage-actions">{children}</div>;
  return inBar ? <CanvasHeaderTools>{actions}</CanvasHeaderTools> : actions;
}

/** The app page: header, role-aware tabs and slots. Content, rights and failures come from props. */
export function AppPage(input: AppPageProps | { pending: true; slots: AppPageProps['slots'] }) {
  const props = 'pending' in input ? null : input;
  const copy = useNamespaceCopy(appPageCopy);
  const ready = useNamespaceCopy(readyCopy);
  const deck = props?.deck === true;
  const tabs = props ? visibleTabs(props.rights, { deck }) : [];
  const current = props ? activeTab(props.tab, props.rights, { deck }) : 'app';
  const actions = new Set<AppAction>(
    props
      ? availableActions(props.rights).filter(
          (action) => typeof props.on[ACTION_HANDLER[action]] === 'function',
        )
      : [],
  );
  const refs = useRef<Partial<Record<AppTab, HTMLButtonElement | null>>>({});
  // Rights can change under a focused control (the Data tab, a menu item). When the
  // focused node is removed, focus would fall to the document: put it on a control that remains.
  const root = useRef<HTMLDivElement | null>(null);
  const lastFocus = useRef<{ node: HTMLElement; fromMenu: boolean } | null>(null);
  useLayoutEffect(() => {
    const was = lastFocus.current;
    if (!was) return;
    const active = document.activeElement;
    if (active?.isConnected && active !== document.body) {
      // Focus is on a live control. If it left the page on purpose, the page no longer owns focus.
      if (!root.current?.contains(active)) lastFocus.current = null;
      return;
    }
    if (was.node.isConnected) return;
    const trigger = root.current?.querySelector<HTMLElement>('[data-testid="app-more"]');
    (was.fromMenu && trigger ? trigger : refs.current[current])?.focus();
    const now = document.activeElement as HTMLElement | null;
    lastFocus.current = now && root.current?.contains(now) ? { node: now, fromMenu: false } : null;
  });
  const onKey = (event: KeyboardEvent) => {
    const next = nextTab(tabs, current, event.key);
    if (!next) return;
    event.preventDefault();
    props?.onTab(next);
    refs.current[next]?.focus();
  };
  const values = { who: props?.app.ownerName ?? '', app: props?.app.name ?? '' };
  // The App's own page shows only the App: the draft, owner and artifact tabs stay reachable from the menu.
  const minimal = props?.actionsInBar === true;
  const secondary = minimal ? tabs.filter((id) => id !== 'app') : [];
  const showTabs = Boolean(props && (!minimal || current !== 'app'));
  return (
    <div
      ref={root}
      className="pc-apppage"
      data-testid="app-page"
      data-role={props?.rights.role}
      onFocusCapture={(event) => {
        const node = event.target as HTMLElement;
        // Remember where the focus came from while it is still attached: a removed menu item no longer has its menu above it.
        lastFocus.current = { node, fromMenu: Boolean(node.closest('.pc-apppage-more')) };
      }}
      onBlurCapture={(event) => {
        const to = event.relatedTarget as Node | null;
        if (to && !event.currentTarget.contains(to)) lastFocus.current = null;
      }}
    >
      {props && (
        <header className="pc-apppage-head">
          <div className="pc-apppage-title">
            {props.on.onBack && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                data-testid="app-back"
                onClick={() => props.on.onBack?.()}
              >
                ‹ {copy.back}
              </Button>
            )}
            {props.app.icon}
            <h1>{props.app.name}</h1>
          </div>
          <ActionsPlace inBar={props.actionsInBar === true}>
            {/* Share is the canvas header's one Share chip (AppSharingChip), not a control of this page. */}
            <MoreMenu
              actions={actions}
              on={props.on}
              activityLabel={ready.tabActivity}
              tabs={secondary}
              onTab={props.onTab}
            />
          </ActionsPlace>
          {!minimal && !deck && (
            <div className="pc-apppage-status">
              {props.savedVersion && (
                <span data-testid="app-saved-version">
                  <StatusPill tone="success">
                    {formatCopy(copy.saved, { n: String(props.savedVersion) })}
                  </StatusPill>
                </span>
              )}
              <p className="pc-apppage-sub">
                {formatCopy(copy.owner, values)} · {props.app.description}
              </p>
            </div>
          )}
        </header>
      )}
      {props && showTabs && (
        <div className="pc-apppage-tabbar">
          <div role="tablist" aria-label={copy.tabsLabel} onKeyDown={onKey}>
            {tabs.map((id) => (
              <button
                key={id}
                ref={(node) => {
                  refs.current[id] = node;
                }}
                type="button"
                role="tab"
                id={`app-tab-${id}`}
                aria-selected={id === current}
                // Only the selected tab's panel is in the page; a pointer to an absent id is invalid ARIA.
                aria-controls={id === current ? `app-panel-${id}` : undefined}
                tabIndex={id === current ? 0 : -1}
                data-testid={`app-tab-${id}`}
                onClick={() => props.onTab(id)}
              >
                {id === 'history'
                  ? ready.tabActivity
                  : id === 'app' && deck
                    ? ready.tabSlides
                    : copy[TAB_KEY[id]]}
              </button>
            ))}
          </div>
          {!minimal && actions.has('change-in-chat') && (
            <div className="pc-apppage-chat pc-apppage-wide">
              {actions.has('open-chat') && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  data-testid="app-open-chat"
                  onClick={() => props.on.onOpenChat?.()}
                >
                  {copy.openChat}
                </Button>
              )}
              <Button
                type="button"
                variant="outline"
                size="sm"
                data-testid="app-change-in-chat"
                onClick={() => props.on.onChangeInChat?.()}
              >
                {copy.changeInChat}
              </Button>
            </div>
          )}
        </div>
      )}
      {props?.failure && props.rights.role !== 'user' && (
        <div data-testid="app-failure">
          <Callout
            tone="danger"
            title={formatCopy(copy.failTitle, {
              n: String(props.failure.version),
              people: String(props.failure.people),
              time: props.failure.time,
            })}
            actions={
              <>
                {props.failure.previousVersion !== null &&
                  props.rights.canPublish &&
                  props.on.onGoBack && (
                    <Button
                      type="button"
                      variant="default"
                      size="sm"
                      data-testid="app-failure-goback"
                      onClick={() => props.on.onGoBack?.()}
                    >
                      {formatCopy(copy.failGoBack, { n: String(props.failure.previousVersion) })}
                    </Button>
                  )}
                {props.on.onFixInChat && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    data-testid="app-failure-fix"
                    onClick={() => props.on.onFixInChat?.()}
                  >
                    {copy.failFix}
                  </Button>
                )}
                {props.on.onDetails && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => props.on.onDetails?.()}
                  >
                    {copy.failDetails}
                  </Button>
                )}
              </>
            }
          />
        </div>
      )}
      <div
        key="panel"
        role="tabpanel"
        id={`app-panel-${current}`}
        // Without the tab bar (the App's own page) the panel is named by the app, not by an absent tab.
        {...(showTabs
          ? { 'aria-labelledby': `app-tab-${current}` }
          : { 'aria-label': props?.app.name })}
        className="pc-apppage-panel"
        data-testid="app-panel"
        data-tab={current}
      >
        {/* The App stays mounted under the other tabs: a look at Data or History keeps its state and scroll. */}
        {input.slots.app !== undefined && (
          <div className="pc-apppage-app" hidden={current !== 'app'}>
            {input.slots.app}
          </div>
        )}
        {current !== 'app' && (input.slots[current] ?? null)}
      </div>
    </div>
  );
}

/** The menu's open state lives in the inner component, so it is discarded when no action remains. */
const none = () => undefined;

function MoreMenu({
  actions,
  on,
  activityLabel,
  tabs,
  onTab,
}: {
  actions: ReadonlySet<AppAction>;
  on: Partial<AppPageHandlers>;
  activityLabel: string;
  tabs: AppTab[];
  onTab: (tab: AppTab) => void;
}) {
  const copy = useNamespaceCopy(appPageCopy);
  const items: [AppAction, string, () => void, boolean?][] = [
    // A handler that is absent was never offered (see ACTION_HANDLER), so `?? none` is unreachable.
    ['open-chat', copy.openChat, on.onOpenChat ?? none, true],
    ['change-in-chat', copy.changeInChat, on.onChangeInChat ?? none, true],
    ['duplicate', copy.duplicate, on.onDuplicate ?? none],
    ['reset', copy.reset, on.onReset ?? none],
    ['unshare', copy.unshare, on.onUnshare ?? none],
  ];
  const shown: [string, string, () => void, boolean?][] = items.filter(([id]) => actions.has(id));
  // Data, History and Versions are not on the App's page: they open from here, and the tab bar returns.
  for (const id of tabs)
    shown.push([
      `tab-${id}`,
      id === 'history' ? activityLabel : copy[TAB_KEY[id]],
      () => onTab(id),
    ]);
  if (shown.length === 0) return null;
  return <MoreMenuOpen shown={shown} />;
}

function MoreMenuOpen({ shown }: { shown: [string, string, () => void, boolean?][] }) {
  const copy = useNamespaceCopy(appPageCopy);
  const [open, setOpen] = useState(false);
  const menu = useMenuBehavior(open, () => setOpen(false));
  return (
    <span
      className="pc-apppage-more"
      ref={menu.root as RefObject<HTMLSpanElement>}
      onKeyDown={menu.onKeyDown}
    >
      <Button
        ref={menu.trigger}
        type="button"
        variant="ghost"
        size="sm"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={copy.moreActions}
        data-testid="app-more"
        onClick={() => setOpen((value) => !value)}
      >
        ⋯
      </Button>
      {open && (
        <div role="menu" className="pc-apppage-menu">
          {shown.map(([id, label, handler, phoneOnly]) => (
            <button
              key={id}
              type="button"
              role="menuitem"
              data-testid={`app-menu-${id}`}
              className={phoneOnly ? 'pc-apppage-phone-only' : undefined}
              onClick={() => {
                setOpen(false);
                handler();
              }}
            >
              {label}
            </button>
          ))}
        </div>
      )}
    </span>
  );
}

/** The deleted-app state: who removed it, how long entries are kept, and where to go. */
export function AppDeleted(props: {
  app: string;
  deletedBy: string;
  /** Already formatted for the viewer. */
  date: string;
  retentionDays: number;
  onLibrary: () => void;
  onAsk: () => void;
}) {
  const copy = useNamespaceCopy(appPageCopy);
  return (
    <section className="pc-apppage-deleted" data-testid="app-deleted">
      <h1>{copy.deletedTitle}</h1>
      <p>
        {formatCopy(copy.deletedBody, {
          who: props.deletedBy,
          app: props.app,
          date: props.date,
          days: String(props.retentionDays),
        })}
      </p>
      <div>
        <Button
          type="button"
          variant="default"
          size="sm"
          data-testid="deleted-library"
          onClick={() => props.onLibrary()}
        >
          {copy.toLibrary}
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          data-testid="deleted-ask"
          onClick={() => props.onAsk()}
        >
          {formatCopy(copy.askPerson, { who: props.deletedBy })}
        </Button>
      </div>
    </section>
  );
}
